import type { ReceptorRecord } from '@trustero/trustero-api-web/lib/agent/receptor_pb'
import {
  Node,
  NodeKind,
} from '@trustero/trustero-api-web/lib/documentsource/documentsource_pb'
import type { ModalFormId } from 'src/components/ModalForms/ModalForm'

/**
 * The document picker browses the repository behind a document receptor
 * (Google Drive, SharePoint, Confluence, ...) through ntrced's DocumentSource
 * service, under the receptor's own identity. These are the picker's own
 * types; nothing here knows a vendor.
 */

export type DocumentNodeKind =
  | 'container'
  | 'folder'
  | 'page'
  | 'file'
  | 'label'

export interface DocumentNode {
  /** Provider key: what folder scans and document links are stored under. */
  id: string
  title: string
  kind: DocumentNodeKind
  /** The parent this node was listed under; ROOT_PARENT_ID for roots. */
  parentId: string
  url?: string
  modifiedAt?: string
  mimeType?: string
  sizeBytes?: number
  /** Provider's own hint; derived expandability lives in the tree state. */
  hasChildren: boolean
  /** The provider's own word: "space", "page", "drive", "folder", "file". */
  providerType?: string
  /** Confluence: owning space id, needed for link import keys. */
  spaceId?: string
}

/** Parent id under which roots are listed. */
export const ROOT_PARENT_ID = ''

/** What the picker is picking: folders (folder scans) or files (linking). */
export type DocumentPickerMode = 'folders' | 'files'

/** The flow that opened the picker; decides where the selection goes. */
export type DocumentPickerSource = 'Evidence' | 'Policy' | 'FolderScan'

export interface DocumentPickerRequest {
  receptor: ReceptorRecord
  mode: DocumentPickerMode
  source: DocumentPickerSource
  /** Subject (e.g. evidence group) the picked files will be linked to. */
  subjectId?: string
  /** Modal to return to after Add/Cancel; derived from source when absent. */
  parentModalId?: ModalFormId
  /**
   * Browse this connection only, with no chooser: the opener already chose
   * it and will act on the pick as that connection's.
   */
  lockConnection?: boolean
}

export interface DocumentPickerSelection {
  receptor: ReceptorRecord
  source: DocumentPickerSource
  nodes: DocumentNode[]
}

const kindFromProto = (kind: NodeKind): DocumentNodeKind => {
  switch (kind) {
    case NodeKind.NODE_KIND_CONTAINER:
      return 'container'
    case NodeKind.NODE_KIND_PAGE:
      return 'page'
    case NodeKind.NODE_KIND_FILE:
      return 'file'
    case NodeKind.NODE_KIND_LABEL:
      return 'label'
    default:
      return 'folder'
  }
}

export const nodeFromProto = (node: Node): DocumentNode => ({
  id: node.getId(),
  title: node.getTitle(),
  kind: kindFromProto(node.getKind()),
  parentId: node.getParentId() || ROOT_PARENT_ID,
  url: node.getUrl() || undefined,
  modifiedAt: node.getModifiedAt() || undefined,
  mimeType: node.getMimeType() || undefined,
  sizeBytes: node.getSizeBytes() || undefined,
  hasChildren: node.getHasChildren(),
  providerType: node.getProviderType() || undefined,
  spaceId: node.getSpaceId() || undefined,
})

/** Nodes that can be opened and can be a folder-scan root. */
export const isContainerLike = (node: DocumentNode): boolean =>
  node.kind === 'container' ||
  node.kind === 'folder' ||
  node.kind === 'page' ||
  node.kind === 'label'

/** Nodes that can be picked when the picker is in files mode. */
export const isLinkable = (node: DocumentNode): boolean =>
  node.kind === 'file' || node.kind === 'page'
